import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { ButtonGroup, Button, makeStyles, Theme, Grid } from '@material-ui/core';

import { useAsset, useVideo } from 'hooks';
import { AssetState, RootState } from 'redux/reducers/type';

import { QuarterListProps, QuarterProps } from 'components/Quarter/type';

import QuarterVO from 'commons/vo/QuarterVO';
import TimeCode from 'commons/utils/TimeCode';
import { style } from 'commons/constants/KeyFlowConstants';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    height: '36px',
    border: `1px solid gray`,
    color: '#999999',
    borderRadius: 0,
    fontSize: '14px',
    '&:hover': {
      backgroundColor: style.PINK_COLOR,
      fontWeight: 'bold',
      color: '#ffffff',
    },
  },
  isActive: {
    border: '1px solid #ffffff !important',
    backgroundColor: style.PINK_COLOR,
    color: '#ffffff',
    fontWeight: 'bold',
  },
}));

function QuarterList({ asset, onClickQuarter, onClickAllQuarter }: QuarterListProps) {
  const classes = useStyles();
  const { onChangeVideoInTime, onChangeVideoCurrentTime, onChangeVideoOutTime } = useVideo();
  const { onSelectQuarter } = useAsset();
  const { quarterList, selectedQuarter } = useSelector(
    (state: RootState) => state.asset,
  );
  const { duration } = useSelector((state: RootState) => state.video);

  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(
    new TimeCode(asset.mediaFps),
  );
  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(
    new TimeCode(asset.mediaFps),
  );
  const [durationTimecodeUtil, setDurationTimecodeUtil] = useState<TimeCode>(
    new TimeCode(asset.mediaFps),
  );

  const isActiveAll = useMemo(() => {
    return selectedQuarter === null;
  }, [selectedQuarter]);

  const onClick = useCallback(
    (qaurter: QuarterVO) => {
      const { inPoint, outPoint, script: keyword } = qaurter;
      inTimecodeUtil.setFrames(inPoint);
      outTimecodeUtil.setFrames(outPoint);
      const inTime = inTimecodeUtil.getSeconds();
      const outTime = outTimecodeUtil.getSeconds();
      onClickQuarter({ inTime, outTime, inPoint, outPoint, keyword });
    },
    [
      onClickQuarter,
      onChangeVideoCurrentTime,
      onChangeVideoInTime,
      onChangeVideoOutTime,
      onSelectQuarter,
    ],
  );

  return (
    <Grid container justifyContent="flex-start" spacing={1}>
      <Grid item xs={4} lg={3}>
        <Button
          fullWidth
          color="inherit"
          onClick={onClickAllQuarter}
          className={clsx(classes.button, isActiveAll && classes.isActive)}
        >
          모두
        </Button>
      </Grid>
      {quarterList.map((quarter: QuarterVO, index: number) => {
        return (
          <Grid key={`${quarter.id}_${index}`} item xs={4} lg={3}>
            <Quarter quarter={quarter} onClick={onClick} />
          </Grid>
        );
      })}
    </Grid>
  );
}

function Quarter({ onClick, quarter }: QuarterProps) {
  const classes = useStyles();

  const { selectedQuarter } = useSelector(({ asset }: RootState): AssetState => asset);
  const { onSelectQuarter } = useAsset();

  const isActive = useMemo(() => {
    if (selectedQuarter) {
      const { id } = selectedQuarter;
      return id === quarter.id;
    }
    return false;
  }, [selectedQuarter, quarter]);

  const onClickQuarter = useCallback(() => {
    onClick(quarter);
    onSelectQuarter(quarter);
  }, [onClick, quarter, onSelectQuarter]);

  return (
    <Button
      fullWidth
      color="inherit"
      className={clsx(classes.button, isActive && classes.isActive)}
      onClick={onClickQuarter}
    >
      {quarter.script}
    </Button>
  );
}

export default QuarterList;
